import { ReactNode } from 'react';

import { CloseButton } from '../../CloseButton';
import { DialogFooter } from '../_DialogFooter';
import { DialogShell } from '../_DialogShell';
import styles from './ActionDialog.module.css';

export interface ActionDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback fired when the dialog is closed (overlay click, escape, close button) */
  onClose: () => void;
  /** Dialog title text */
  title: string;
  /** Supporting text rendered directly below the title — omit to hide */
  description?: string;
  /** Dialog content — any arbitrary node (list, form fields, custom content) */
  children: ReactNode;
  /** Label for the primary (brand) confirm button */
  primaryLabel?: string;
  /** Callback fired when the primary CTA is clicked */
  onPrimaryAction?: () => void;
  /** Whether the primary CTA shows a loader */
  isPrimaryLoading?: boolean;
  /** Whether the primary CTA is disabled */
  isPrimaryDisabled?: boolean;
  /** Label for the secondary (tertiary) cancel button — omit to hide */
  secondaryLabel?: string;
  /** Callback fired when the secondary CTA is clicked */
  onSecondaryAction?: () => void;
  /** Whether the secondary CTA shows a loader */
  isSecondaryLoading?: boolean;
  /** Label for the left-aligned Back button (tertiary + chevron) — omit to hide */
  backLabel?: string;
  /** Callback fired when Back is clicked */
  onBack?: () => void;
  /** Whether the dialog is centered vertically */
  isCentered?: boolean;
  /** Dialog content width */
  size?: string | number;
  /** Whether to close the dialog when clicking outside */
  shouldCloseOnClickOutside?: boolean;
  /** Whether to close the dialog when pressing Escape */
  shouldCloseOnEscape?: boolean;
  className?: string;
}

/**
 * ActionDialog — content-slot dialog built on the shared Dialog shell. Renders a
 * title, an arbitrary `children` content region, and a split footer (optional
 * Back on the left, optional Cancel + brand Confirm on the right).
 */
export function ActionDialog({
  isOpen,
  onClose,
  title,
  description,
  children,
  primaryLabel = 'Confirm',
  onPrimaryAction,
  isPrimaryLoading,
  isPrimaryDisabled,
  secondaryLabel = 'Cancel',
  onSecondaryAction,
  isSecondaryLoading,
  backLabel = 'Back',
  onBack,
  isCentered = true,
  size = 640,
  shouldCloseOnClickOutside,
  shouldCloseOnEscape,
  className,
}: ActionDialogProps) {
  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      isCentered={isCentered}
      size={size}
      shouldCloseOnClickOutside={shouldCloseOnClickOutside}
      shouldCloseOnEscape={shouldCloseOnEscape}
      className={`${styles.root} ${className ?? ''}`}
    >
      <div className={styles.headerRow}>
        <div className={styles.titleColumn}>
          <p className={styles.title}>{title}</p>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        <CloseButton onClick={onClose} aria-label="Close" />
      </div>
      <div className={styles.content}>{children}</div>
      <DialogFooter
        backLabel={backLabel}
        onBack={onBack}
        secondaryLabel={secondaryLabel}
        onSecondaryAction={onSecondaryAction}
        isSecondaryLoading={isSecondaryLoading}
        primaryLabel={primaryLabel}
        onPrimaryAction={onPrimaryAction}
        isPrimaryLoading={isPrimaryLoading}
        isPrimaryDisabled={isPrimaryDisabled}
      />
    </DialogShell>
  );
}

ActionDialog.displayName = 'ActionDialog';
